<template>
    <v-container class="max-height my-5">
        <v-row>
            <h1 class="text-left">Maintenance Cost Summary</h1>
        </v-row>

        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="3" sm="6">
                <company-select v-model="formData['company']" :items="companiesList" density="compact" label="Select Company"
                          @update:model-value="()=>{fetchAssetList();fetchReport()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['assets']" :items="assetsList" chips clearable
                          density="compact" item-title="label" item-value="id" label="Select Asset(s)" multiple
                          @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    prepend-icon=""
                    hide-actions
                    label="Start Date"
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    density="compact"
                    label="End Date"
                    prepend-icon=""
                    clearable
                    hide-actions
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>

            <v-col cols="auto" class="d-flex align-center">
                <v-btn :loading="downloading" prepend-icon="mdi-download" size="small" variant="tonal"
                       @click="exportCsv">Export CSV</v-btn>
            </v-col>
            <v-col class="float-end" cols="1">
                <v-btn :icon="`mdi-${graphView?'table':'chart-line'}`" size="small" @click="graphView = !graphView"/>
            </v-col>
        </v-row>

        <v-row v-if="!graphView">
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Maintenance Cost</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>
                            <router-link v-if="item.asset_id" :to="{name: 'asset', params: {asset: item.asset_id}, query: {tab: 1}}" target="_blank">{{ item.asset }}</router-link>
                            <template v-else>{{ item.asset }}</template>
                        </td>
                        <td>{{ item.total_cost }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="!reportData" class="text-center" colspan="2">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="2">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>

        </v-row>
        <v-row v-else>
            <v-col cols="12">
                <Bar :data="chartData" :options="chartOptions" height="100"/>
            </v-col>
        </v-row>
        <div class="my-5">&nbsp;</div>
    </v-container>
</template>

<script setup>
import {Bar} from "vue-chartjs";
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js'
import {useAuthStore} from "@/stores/authStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import CompanySelect from "@/components/globals/company-select.vue";


Chart.register(...registerables)


const {user} = useAuthStore();
const {downloadReport} = useReportExport();
const graphView = ref(true);
const downloading = ref(false);
const reportData = ref([]);
const assetsList = ref([]);
const companiesList = ref([]);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    assets: null,
})

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchAssetList();
    }
})

const chartOptions = {
    responsive: true,
    plugins: {
        tooltip: {
            callbacks: {
                // Add 'km' to tooltip values
                label: function (context) {
                    const value = context.raw; // The value of the dataset
                    return `$ ${value}`; // Append unit
                }
            }
        }
    },
    scales: {
        y: {
            beginAtZero: true,
            precision: 0,
            ticks: {
                stepSize: 1,
                callback: function (value) {
                    return `$${value}`; // Append unit
                }
            }
        }
    }
};
const chartData = computed(() => {
    if (!reportData.value.length) {
        return {
            labels: [],
            datasets: [{
                data: [],
                label: 'Maintenance Cost',
                backgroundColor: '#673AB7',
            }]
        };
    }
    const labels = reportData.value.map(item => item.asset)
    const total_cost = reportData.value.map(item => item.total_cost)

    return {
        labels,
        datasets: [{
            data: total_cost,
            label: 'Maintenance Cost',
            backgroundColor: '#673AB7',
        }]
    }
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assetsList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const fetchReport = () => {
    send('get', `fleet/reports/assets-maintenance-cost-summary`, formData)
        .then((response) => {
            reportData.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Summary report could not be loaded', 'error')
        })
}

const exportCsv = async () => {
    if (!formData['company']) {
        showSnackbar('Company is required', 'error')
        return
    }
    downloading.value = true
    try {
        await downloadReport('fleet/reports/assets-maintenance-cost-summary', formData, 'csv', 'maintenance-cost-summary.csv')
    } catch (e) {
        showSnackbar('Export failed', 'error')
    } finally {
        downloading.value = false
    }
}
</script>
